import { Component, ChangeDetectionStrategy, inject } from '@angular/core';

import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  Transaction,
  TransactionTypeEnum,
  TransactionAdjustmentReasonEnum,
} from '@olv/ui-shared/models/merchant-transaction.model';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface AdjustTransactionDialogResult {
  adjustmentType: TransactionTypeEnum;
  amount: number;
  reason: TransactionAdjustmentReasonEnum;
}

@Component({
  selector: 'app-adjust-transaction-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatIconModule, DropdownComponent, TextInputComponent, ButtonComponent],
  templateUrl: './adjust-transaction-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./adjust-transaction-dialog.component.scss'],
})
export class AdjustTransactionDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly formErrorService = inject(FormErrorService);
  private readonly dialogRef =
    inject<MatDialogRef<AdjustTransactionDialogComponent, AdjustTransactionDialogResult>>(MatDialogRef);
  readonly data = inject<{
    transaction: Transaction;
  }>(MAT_DIALOG_DATA);

  readonly adjustmentForm: FormGroup;

  readonly adjustmentTypeOptions = [
    { value: TransactionTypeEnum.CreditAdjustment, label: 'Credit Adjustment' },
    { value: TransactionTypeEnum.DebitAdjustment, label: 'Debit Adjustment' },
  ];

  readonly adjustmentReasonOptions = [
    {
      value: TransactionAdjustmentReasonEnum.AccountOverspent,
      label: 'Account Overspent',
    },
    {
      value: TransactionAdjustmentReasonEnum.MerchantGoodwill,
      label: 'Merchant Goodwill',
    },
  ];

  constructor() {
    this.adjustmentForm = this.fb.group({
      adjustmentType: [null, Validators.required],
      amount: ['', [Validators.required, this.maxTransactionAmountValidator.bind(this)]],
      adjustmentReason: [null, Validators.required],
    });
  }

  private maxTransactionAmountValidator(control: AbstractControl) {
    const value = Number(control.value);
    const max = Number(this.data?.transaction?.amountBaseCurrency);

    if (!value || !max) return null;
    return value > max ? { maxAmountExceeded: true } : null;
  }

  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.adjustmentForm.get(controlName), fieldName);
  }

  get nextButtonState(): 'disabled' | 'default' {
    return this.adjustmentForm.invalid ? 'disabled' : 'default';
  }

  onSubmit(): void {
    if (this.adjustmentForm.invalid) {
      this.adjustmentForm.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      adjustmentType: this.adjustmentForm.value.adjustmentType,
      amount: Number(this.adjustmentForm.value.amount),
      reason: this.adjustmentForm.value.adjustmentReason,
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
